import React, { Suspense } from 'react';
import { GraphSkeleton, GraphErrorState, GraphFrame } from '../graph-sdk';
import { getGraphEntry, getGraphIds } from './registry';
import type { GraphComponentProps } from './registry';

export interface GraphWidgetProps {
  /** Graph ID from registry */
  graphId: string;
  /** Session ID for data fetching */
  sessionId: string | null;
  /** Optional parameter info for corrections */
  parameterInfo?: any;
  /** Optional className for styling */
  className?: string;
  /** Optional rower context for rower-focused visualizations */
  rowerContext?: import('../../types/rowerContext').RowerContext | null;
  /** Concept2 Logbook workout id for graphs that consume erg stroke data */
  concept2WorkoutId?: string | null;
  /**
   * When true (default), charts that support it fill a fixed-height tile and scroll internally.
   * Set false for full-width starter-dashboard sections where the page should scroll instead.
   */
  tileMode?: boolean;
}

/**
 * GraphWidget: Dynamic graph loader
 *
 * Looks up a graph in the registry and lazy-loads it.
 * Charts are responsible for their own UI wrapping (ChartContainer).
 * This component is ONLY for registry lookup + lazy loading.
 *
 * Usage:
 * ```tsx
 * <GraphWidget
 *   graphId="power-analysis"
 *   sessionId={sessionId}
 *   parameterInfo={parameterInfo}
 * />
 * ```
 */
export const GraphWidget: React.FC<GraphWidgetProps> = ({
  graphId,
  sessionId,
  parameterInfo,
  className = '',
  rowerContext,
  concept2WorkoutId,
  tileMode,
}) => {
  // Look up graph in registry
  const entry = getGraphEntry(graphId);

  // Handle unknown graph ID - show error in a frame
  if (!entry) {
    return (
      <GraphFrame title="Error" subtitle="Graph not found">
        <GraphErrorState
          title="Unknown Graph"
          error={`Graph "${graphId}" not found in registry. Available graphs: ${getGraphIds().join(', ')}`}
        />
      </GraphFrame>
    );
  }

  // Get the lazy-loaded component
  const GraphComponent = entry.component;

  // Prepare props for the graph component
  const graphProps: GraphComponentProps = {
    sessionId,
    parameterInfo,
    className,
    rowerContext,
    concept2WorkoutId,
    tileMode: tileMode ?? true,
  };

  // Render loading state (simple skeleton, chart will add its own frame)
  const loadingState = <GraphSkeleton title={`Loading ${entry.name}...`} />;

  // Render the graph with Suspense
  // Chart components handle their own ChartContainer/frame
  return (
    <Suspense fallback={loadingState}>
      <GraphComponent {...graphProps} />
    </Suspense>
  );
};

export default GraphWidget;
